import { useRouter } from 'expo-router';
import { useState } from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useResponsive } from '../../../hooks/useResponsive';
import { COLORS } from '../../../styles/colors';
import { fontFamilies } from '../../../utils/fontLoader';
import ContactUsForm from '../../common/ContactUsForm';

/**
 * The footer section of the landing page.
 * @param {object} props - The component props.
 * @param {object} props.content - The content object for this section.
 * @returns {JSX.Element}
 */
const Footer = ({ content, onNavPress, style, variant = 'light', compact = false, hideLogo = false }) => {
  const { isMobile } = useResponsive();
  const router = useRouter();
  const [isFormVisible, setFormVisible] = useState(false);
  const LogoComponent =
    variant === 'dark' ? content.logoLight?.default || content.logoLight : content.logo.default || content.logo;
  const isDark = variant === 'dark';

  const handleContactClick = () => {
    setFormVisible(true);
  };

  return (
    <>
      <View
        style={[
          styles.footerContainer,
          isMobile && styles.footerContainerMobile,
          isDark && styles.footerContainerDark,
          compact && styles.footerContainerCompact,
          compact && isMobile && styles.footerContainerCompactMobile,
          style,
        ]}
      >
        {!hideLogo && (
          <View style={styles.logo} accessibilityRole="banner">
            <LogoComponent width={110} height={24} accessibilityLabel="Doocars Logo" />
          </View>
        )}
        <View
          style={[styles.footerLinks, isMobile && styles.footerLinksMobile]}
          accessibilityRole="navigation"
        >
          {content.links.map((link) => {
            if (Platform.OS === 'web' && link.target) {
              return (
                <a
                  key={link.title}
                  href={link.target}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavPress ? onNavPress(link.target) : router.push(link.target);
                  }}
                  style={{ textDecoration: 'none' }}
                >
                  <Text style={[styles.footerLinkText, isDark && styles.footerLinkTextDark]}>
                    {link.title}
                  </Text>
                </a>
              );
            }
            return (
              <TouchableOpacity
                key={link.title}
                onPress={() => {
                  if (link.title === 'Contact us') {
                    handleContactClick();
                  } else if (link.target) {
                    onNavPress ? onNavPress(link.target) : router.push(link.target);
                  }
                }}
                accessibilityRole="link"
              >
                <Text style={[styles.footerLinkText, isDark && styles.footerLinkTextDark]}>
                  {link.title}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
      <ContactUsForm visible={isFormVisible} onClose={() => setFormVisible(false)} />
    </>
  );
};

const styles = StyleSheet.create({
  footerContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 120,
    borderTopWidth: 1,
    borderTopColor: '#EEE',
    backgroundColor: '#F8F9FD',
  },
  footerContainerMobile: {
    flexDirection: 'column',
    gap: 32,
    paddingHorizontal: 24,
  },
  logo: {
    width: 110,
    height: 28,
    zIndex: 1,
  },
  footerLinks: {
    flexDirection: 'row',
    gap: 32,
  },
  footerLinksMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  },
  footerLinkText: {
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 14,
    color: COLORS.darkGray,
  },
  footerContainerDark: {
    backgroundColor: 'transparent',
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
  },
  footerContainerCompact: {
    paddingHorizontal: 32,
    paddingVertical: 20,
  },
  footerContainerCompactMobile: {
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  footerLinkTextDark: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
});

export default Footer;
